import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { createContext, useCallback, useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { closePopup, openPopup } from "../../store/slices/ApplicationSlice";

import { AuthProvider } from "./AuthContext";
import { DashboardProvider } from "./DashboardContext";
import { NotificationProvider } from "./NotificationContext";
import { PortalProvider } from "./PortalContext";
import { RemoteRenderProvider } from "./RemoteRenderContext";

import { SidebarSize } from "../enums/SideBarsEnum";

import { getToken } from "../utils/TokenUtils";
import { CustomMeasurementsProvider } from "./CustomMeasurementsContext";
import { MeasurementsProvider } from "./MeasurementsContext";
import { PlanProvider } from "./PlanContext";
import { ReportProvider } from "./ReportContext";
import { UserProvider } from "./UserContext";
import { useRemoteRenderNotifications } from "../../hooks/useRemoteRenderNotifications";

const AppContext = createContext();

// Create Apollo Client instance outside of component to avoid recreating on every render
const createApolloClient = () => {
  const httpLink = createHttpLink({
    uri: `${process.env.REACT_APP_BASE_URL || window.location.origin}/gql`,
  });

  const authLink = setContext(async (_, { headers }) => {
    // Trust sessionStorage - onUserLoaded (in AuthContext) keeps it fresh via automaticSilentRenew
    // If token is expired and request fails, server returns 401 (handle gracefully in error link)
    const token = getToken();

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  return new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            patientsByFiltering: {
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "network-only",
      },
      query: {
        fetchPolicy: "network-only",
      },
    },
  });
};

const queriesClient = createApolloClient();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

const AppUiProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupComponent, setPopupComponent] = useState(null);
  const [leftWidgetComponent, setLeftWidgetComponent] = useState(null);
  const [rightWidgetComponent, setRightWidgetComponent] = useState(null);
  const [showRightSidebar, setShowRightSidebar] = useState(false);
  const [showLeftSidebar, setShowLeftSidebar] = useState(false);
  const [patientDashboardWidgets, setPatientDashboardWidgets] = useState([]);
  const [gridDimensions, setGridDimensions] = useState({ width: 0, height: 0 });
  const [leftSidebarWidth, setLeftSidebarWidth] = useState(SidebarSize.MEDIUM);
  const [rightSidebarWidth, setRightSidebarWidth] = useState(
    SidebarSize.MEDIUM
  );

  const showPopup = useCallback(
    component => {
      setPopupComponent(component);
      setPopupVisible(true);
      dispatch(openPopup());
    },
    [dispatch]
  );

  const hidePopup = useCallback(() => {
    setPopupComponent(null);
    setPopupVisible(false);
    dispatch(closePopup());
  }, [dispatch]);

  const hideSidebars = useCallback(() => {
    setShowLeftSidebar(false);
    setShowRightSidebar(false);
    setLeftWidgetComponent(null);
    setRightWidgetComponent(null);
  }, []);

  const showOnlyLeftSidebar = useCallback(
    (component, width = SidebarSize.MEDIUM) => {
      setLeftWidgetComponent(component);
      setLeftSidebarWidth(width);
      setShowLeftSidebar(true);
      setShowRightSidebar(false);
    },
    []
  );

  const showOnlyRightSidebar = useCallback(
    (component, width = SidebarSize.MEDIUM) => {
      setRightWidgetComponent(component);
      setRightSidebarWidth(width);
      setShowLeftSidebar(false);
      setShowRightSidebar(true);
    },
    []
  );

  const ShowBothSidebars = useCallback(
    (
      leftComponent,
      rightComponent,
      leftWidth = SidebarSize.MEDIUM,
      rightWidth = SidebarSize.MEDIUM
    ) => {
      setLeftWidgetComponent(leftComponent);
      setLeftSidebarWidth(leftWidth);
      setRightWidgetComponent(rightComponent);
      setRightSidebarWidth(rightWidth);
      setShowLeftSidebar(true);
      setShowRightSidebar(true);
    },
    []
  );

  const contextValue = {
    popupVisible,
    showPopup,
    hidePopup,
    popupComponent,
    leftWidgetComponent,
    rightWidgetComponent,
    showLeftWidget: setLeftWidgetComponent,
    hideLeftWidget: () => setLeftWidgetComponent(null),
    showRightWidget: setRightWidgetComponent,
    showRightSidebar,
    showLeftSidebar,
    hideRightWidget: () => setRightWidgetComponent(null),
    hideSidebars,
    showOnlyRightSidebar,
    showOnlyLeftSidebar,
    ShowBothSidebars,
    patientDashboardWidgets,
    setPatientDashboardWidgets,
    gridDimensions,
    setGridDimensions,
    leftSidebarWidth,
    setLeftSidebarWidth,
    rightSidebarWidth,
    setRightSidebarWidth,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// Component that initializes remote render notifications
// Must be a child of both RemoteRenderProvider and NotificationProvider
const RemoteRenderNotificationBridge = ({ children }) => {
  useRemoteRenderNotifications();
  return children;
};

// Main provider component with proper nesting order
export const AppProvider = ({ children }) => {
  return (
    <ApolloProvider client={queriesClient}>
      <AuthProvider>
        <UserProvider>
          <AppUiProvider>
            <DashboardProvider>
              <MeasurementsProvider>
                <CustomMeasurementsProvider>
                  <PlanProvider>
                    <RemoteRenderProvider>
                      <ReportProvider>
                        <PortalProvider>
                          <NotificationProvider>
                            <RemoteRenderNotificationBridge>
                              {children}
                            </RemoteRenderNotificationBridge>
                          </NotificationProvider>
                        </PortalProvider>
                      </ReportProvider>
                    </RemoteRenderProvider>
                  </PlanProvider>
                </CustomMeasurementsProvider>
              </MeasurementsProvider>
            </DashboardProvider>
          </AppUiProvider>
        </UserProvider>
      </AuthProvider>
    </ApolloProvider>
  );
};
